import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { FaDollarSign, FaCommentAlt, FaBuilding, FaCalendarAlt, FaExclamationTriangle } from 'react-icons/fa';
import PageHeader from '../../components/PageHeader';
import Table from '../../components/Table';
import StatCard from '../../components/StatCard';
import Skeleton from '../../components/Skeleton';
import { costService, RANGE_PRESETS, today, daysAgo } from '../../services/costService';
import { toastError } from '../../utils/toast';

const PAGE_SIZE = 50;

const cardStyle = {
  background: 'linear-gradient(160deg, #161731 0%, #12132a 55%, #0f1024 100%)',
  border: '1px solid rgba(255,255,255,0.07)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
  borderRadius: '12px',
  padding: '20px',
  position: 'relative',
  overflow: 'hidden',
};

const RANGE_LABELS = { today: 'Hoy', '7d': '7 días', month: 'Mes actual', year: 'Año', custom: 'Custom' };

const fmtUSD = (v) => `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMXN = (v) => v == null ? '—' : `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`;
const fmtShortDate = (s) => {
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
};
const fmtDateTime = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const fmtCost = (v) => v == null ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })}`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.08)' }}>
      <p className="mb-1 font-semibold" style={{ color: '#9BA3B8' }}>{fmtShortDate(label)}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <strong>{p.dataKey === 'total_cost_usd' ? fmtUSD(p.value) : p.value.toLocaleString()}</strong>
        </p>
      ))}
    </div>
  );
};

const AdminCostosPage = () => {
  const [rangeMode, setRangeMode] = useState('month');
  const [customStart, setCustomStart] = useState(daysAgo(29));
  const [customEnd, setCustomEnd] = useState(today());

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState([]);
  const [projectsInfo, setProjectsInfo] = useState({});

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);

  const [coverage, setCoverage] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailCount, setDetailCount] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadingMore, setDetailLoadingMore] = useState(false);
  const detailRef = useRef(null);

  const { start, end } = useMemo(() => {
    if (rangeMode === 'custom') return { start: customStart, end: customEnd };
    return RANGE_PRESETS[rangeMode]();
  }, [rangeMode, customStart, customEnd]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, info] = await Promise.all([
        costService.getSummaryByRange(start, end),
        costService.getProjectsInfo(),
      ]);
      setProjectsInfo(info);
      const enriched = rows
        .map(r => ({ ...r, name: info[r.project_id]?.name || `Proyecto ${r.project_id}`, price: info[r.project_id]?.price ?? null }))
        .sort((a, b) => b.total_cost_usd - a.total_cost_usd);
      setSummary(enriched);
      if (!selectedProjectId && enriched.length > 0) setSelectedProjectId(enriched[0].project_id);
    } catch (err) {
      toastError(err.message || 'Error al cargar consumo');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Gráfica, cobertura y detalle de ejecuciones: todo sigue el mismo rango de fechas
  // elegido arriba (antes la gráfica quedaba fija a 30 días sin importar el filtro).
  useEffect(() => {
    if (!selectedProjectId) return;
    setSeriesLoading(true);
    costService.getDailySeries(selectedProjectId, start, end)
      .then(setSeries)
      .catch(err => toastError(err.message || 'Error al cargar serie diaria'))
      .finally(() => setSeriesLoading(false));

    setDetailLoading(true);
    setCoverage(null);
    Promise.all([
      costService.getMessageCoverage(selectedProjectId, start, end),
      costService.getExecutionDetail(selectedProjectId, start, end, { limit: PAGE_SIZE, offset: 0 }),
    ])
      .then(([cov, det]) => {
        setCoverage(cov);
        setDetailRows(det.rows);
        setDetailCount(det.count);
      })
      .catch(err => toastError(err.message || 'Error al cargar detalle de ejecuciones'))
      .finally(() => setDetailLoading(false));
  }, [selectedProjectId, start, end]);

  const handleSelectProject = (projectId) => {
    setSelectedProjectId(projectId);
    requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const handleLoadMoreDetail = async () => {
    if (!selectedProjectId) return;
    setDetailLoadingMore(true);
    try {
      const det = await costService.getExecutionDetail(selectedProjectId, start, end, { limit: PAGE_SIZE, offset: detailRows.length });
      setDetailRows(prev => [...prev, ...det.rows]);
    } catch (err) {
      toastError(err.message || 'Error al cargar más ejecuciones');
    } finally {
      setDetailLoadingMore(false);
    }
  };

  const totals = useMemo(() => summary.reduce((acc, r) => ({
    cost: acc.cost + r.total_cost_usd,
    messages: acc.messages + r.total_messages,
  }), { cost: 0, messages: 0 }), [summary]);

  const columns = [
    {
      key: 'name',
      title: 'Proyecto',
      sortable: true,
      render: (v) => <span className="font-medium" style={{ color: '#E8EAF0' }}>{v}</span>,
    },
    {
      key: 'total_cost_usd',
      title: 'Costo',
      sortable: true,
      render: (v) => <span className="text-sm font-mono font-semibold" style={{ color: '#4ADE80' }}>{fmtUSD(v)}</span>,
    },
    {
      key: 'total_messages',
      title: 'Mensajes (con costo)',
      sortable: true,
      render: (v) => <span className="text-sm font-mono" style={{ color: '#C8CDD9' }}>{(v || 0).toLocaleString()}</span>,
    },
    {
      key: 'total_prompt_tokens',
      title: 'Tokens prompt',
      sortable: true,
      render: (v) => <span className="text-xs font-mono" style={{ color: '#60A5FA' }}>{(v || 0).toLocaleString()}</span>,
    },
    {
      key: 'total_completion_tokens',
      title: 'Tokens completion',
      sortable: true,
      render: (v) => <span className="text-xs font-mono" style={{ color: '#A78BFA' }}>{(v || 0).toLocaleString()}</span>,
    },
    {
      key: 'price',
      title: 'Mensualidad',
      sortable: true,
      render: (v) => <span className="text-xs font-mono" style={{ color: '#6B7391' }}>{fmtMXN(v)}</span>,
    },
    {
      key: 'project_id',
      title: '',
      sortable: false,
      render: (v, row) => (
        <button
          onClick={() => handleSelectProject(v)}
          className="text-xs font-medium px-2.5 py-1 rounded-lg transition-colors"
          style={{
            backgroundColor: selectedProjectId === v ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.04)',
            color: selectedProjectId === v ? '#60A5FA' : '#6B7391',
          }}
        >
          Ver detalle
        </button>
      ),
    },
  ];

  const selectedInfo = selectedProjectId ? projectsInfo[selectedProjectId] : null;

  return (
    <div className="space-y-6">
      <PageHeader title="Consumo por agente / proyecto" subtitle="Costo real de OpenAI (USD) capturado por conversación — solo visible para administradores" />

      {/* Selector de rango */}
      <div className="flex flex-wrap items-center gap-2">
        {Object.keys(RANGE_LABELS).filter(k => k !== 'custom').map(key => (
          <button
            key={key}
            onClick={() => setRangeMode(key)}
            className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
            style={{
              backgroundColor: rangeMode === key ? 'rgba(220,31,38,0.15)' : 'rgba(255,255,255,0.04)',
              color: rangeMode === key ? '#F87171' : '#9BA3B8',
              border: `1px solid ${rangeMode === key ? 'rgba(220,31,38,0.3)' : 'rgba(255,255,255,0.06)'}`,
            }}
          >
            {RANGE_LABELS[key]}
          </button>
        ))}
        <button
          onClick={() => setRangeMode('custom')}
          className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
          style={{
            backgroundColor: rangeMode === 'custom' ? 'rgba(220,31,38,0.15)' : 'rgba(255,255,255,0.04)',
            color: rangeMode === 'custom' ? '#F87171' : '#9BA3B8',
            border: `1px solid ${rangeMode === 'custom' ? 'rgba(220,31,38,0.3)' : 'rgba(255,255,255,0.06)'}`,
          }}
        >
          <FaCalendarAlt size={10} /> Custom
        </button>
        {rangeMode === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={customStart} max={customEnd} onChange={e => setCustomStart(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-lg" style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.08)', color: '#C8CDD9' }} />
            <span className="text-xs" style={{ color: '#4A5270' }}>→</span>
            <input type="date" value={customEnd} min={customStart} max={today()} onChange={e => setCustomEnd(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-lg" style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.08)', color: '#C8CDD9' }} />
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard icon={FaDollarSign} label={`Costo total — ${RANGE_LABELS[rangeMode]}`} value={fmtUSD(totals.cost)} color="#4ADE80" accent />
        <StatCard icon={FaCommentAlt} label="Mensajes con costo" value={totals.messages} color="#3B82F6" />
        <StatCard icon={FaBuilding} label="Proyectos con actividad" value={summary.length} color="#A78BFA" />
      </div>

      {/* Tabla resumen */}
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} style={cardStyle}>
        {loading ? (
          <Skeleton.TableRows rows={5} cols={6} />
        ) : (
          <Table
            data={summary}
            columns={columns}
            options={{ pagination: false, showSearch: true, searchPlaceholder: 'Buscar proyecto...', emptyMessage: 'Sin consumo registrado en este rango' }}
          />
        )}
      </motion.div>

      {/* Detalle del proyecto seleccionado: gráfica + cobertura + ejecuciones */}
      <div ref={detailRef} className="space-y-6">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: 0.1 }} style={cardStyle}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#4A5270' }}>
              Costo diario — {RANGE_LABELS[rangeMode]} ({fmtShortDate(start)} → {fmtShortDate(end)}) {selectedInfo?.name ? `· ${selectedInfo.name}` : ''}
            </p>
          </div>
          {!selectedProjectId ? (
            <p className="text-xs py-8 text-center" style={{ color: '#3E4460' }}>Selecciona un proyecto en la tabla para ver el detalle.</p>
          ) : seriesLoading ? (
            <Skeleton.Card height="220px" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradCost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4ADE80" stopOpacity={0.30} />
                    <stop offset="100%" stopColor="#4ADE80" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tickFormatter={fmtShortDate} tick={{ fill: '#3E4460', fontSize: 10 }} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(series.length / 8))} />
                <YAxis tick={{ fill: '#3E4460', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, color: '#4A5270', paddingTop: 8 }} />
                <Area type="monotone" dataKey="total_cost_usd" name="Costo (USD)" stroke="#4ADE80" strokeWidth={2} fill="url(#gradCost)" dot={false} activeDot={{ r: 4 }} animationDuration={1200} animationEasing="ease-out" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {selectedProjectId && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: 0.15 }} style={cardStyle}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#4A5270' }}>
                Detalle por ejecución {selectedInfo?.name ? `· ${selectedInfo.name}` : ''}
              </p>
              {coverage && (
                <div className="flex items-center gap-2 text-xs" style={{ color: '#9BA3B8' }}>
                  <span>{coverage.total.toLocaleString()} mensajes tipo agente</span>
                  <span style={{ color: '#4A5270' }}>·</span>
                  <span style={{ color: '#4ADE80' }}>{coverage.withCost.toLocaleString()} con costo capturado</span>
                  {coverage.total > coverage.withCost && (
                    <span className="flex items-center gap-1" style={{ color: '#FBBF24' }}>
                      <FaExclamationTriangle size={10} />
                      {(coverage.total - coverage.withCost).toLocaleString()} sin costo (capture_metrics=false)
                    </span>
                  )}
                </div>
              )}
            </div>

            {detailLoading ? (
              <Skeleton.TableRows rows={6} cols={7} />
            ) : detailRows.length === 0 ? (
              <p className="text-xs py-8 text-center" style={{ color: '#3E4460' }}>Sin ejecuciones tipo agente en este rango.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                        {['Fecha', 'Modelo', 'Tokens prompt', 'Tokens completion', 'Costo', 'Latencia', 'Estado', 'Execution ID'].map(h => (
                          <th key={h} className="text-left py-2 px-2 font-semibold uppercase tracking-wide" style={{ color: '#4A5270', fontSize: '10px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detailRows.map(row => {
                        const hasCost = row.cost_usd != null;
                        return (
                          <tr key={row.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', opacity: hasCost ? 1 : 0.55 }}>
                            <td className="py-2 px-2 whitespace-nowrap" style={{ color: '#C8CDD9' }}>{fmtDateTime(row.created_at)}</td>
                            <td className="py-2 px-2 font-mono whitespace-nowrap" style={{ color: '#9BA3B8' }}>{row.model || '—'}</td>
                            <td className="py-2 px-2 font-mono" style={{ color: '#60A5FA' }}>{row.prompt_tokens?.toLocaleString() ?? '—'}</td>
                            <td className="py-2 px-2 font-mono" style={{ color: '#A78BFA' }}>{row.completion_tokens?.toLocaleString() ?? '—'}</td>
                            <td className="py-2 px-2 font-mono font-semibold" style={{ color: hasCost ? '#4ADE80' : '#FBBF24' }}>
                              {hasCost ? fmtCost(row.cost_usd) : 'sin costo'}
                            </td>
                            <td className="py-2 px-2 font-mono" style={{ color: '#6B7391' }}>{row.latency_ms != null ? `${row.latency_ms} ms` : '—'}</td>
                            <td className="py-2 px-2" style={{ color: '#6B7391' }}>{row.execution_status || '—'}</td>
                            <td className="py-2 px-2 font-mono truncate max-w-[140px]" style={{ color: '#4A5270' }} title={row.execution_id || ''}>{row.execution_id || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  <p className="text-[11px]" style={{ color: '#4A5270' }}>
                    Mostrando {detailRows.length} de {detailCount.toLocaleString()} ejecuciones
                  </p>
                  {detailRows.length < detailCount && (
                    <button
                      onClick={handleLoadMoreDetail}
                      disabled={detailLoadingMore}
                      className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                      style={{ backgroundColor: 'rgba(59,130,246,0.12)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.25)' }}
                    >
                      {detailLoadingMore ? 'Cargando...' : 'Cargar más'}
                    </button>
                  )}
                </div>
              </>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminCostosPage;
